import {
  ChangeDetectionStrategy,
  Component,
  DoCheck,
  ElementRef,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import escapeRegExp from 'lodash-es/escapeRegExp';

@Component({
  selector: 'fl-highlight-text',
  template: `
    <span class="RawContent" #rawContent><ng-content></ng-content></span>
    <span #container></span>
  `,
  styleUrls: ['./highlight-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HighlightTextComponent implements DoCheck, OnChanges {
  private text?: string;
  /**
   * The text to mark.
   *
   * @type string
   * @memberof HighlightTextComponent
   */
  @Input() mark: string;
  // TODO: Technically we shouldn't need the raw content to be inserted in a
  // DOM and get the text that way, but because dynamic content projection isn't
  // out to provide us with a `*` selector to select all ContentChildren and
  // check the raw text, we'll settle with this.
  // Check https://github.com/angular/angular/issues/8563 for updates.
  @ViewChild('rawContent') private rawContent: ElementRef;
  @ViewChild('container') private container: ElementRef;

  constructor(private renderer: Renderer2) {}

  ngDoCheck() {
    // Check if the text is different so we know to change the text and the
    // highlighted text.
    const checkedText = this.getText();
    if (this.text !== checkedText) {
      this.text = checkedText;
      this.generateTextWithHighlight(this.text, this.mark);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // If the marked text changes recalculate as well.
    const properties = Object.keys(changes);
    if (properties.includes('mark')) {
      this.generateTextWithHighlight(this.text, changes.mark.currentValue);
    }
  }

  /**
   * Get the text of the content. This means no HTML at all, just text.
   *
   * @private
   * @returns
   * @memberof HighlightTextComponent
   */
  private getText() {
    const element = this.rawContent.nativeElement;
    return element.textContent.trim();
  }

  /**
   * Makes a normal text element in a <span> tag.
   *
   * @private
   * @param {string} text The text to wrap in the tag.
   * @returns A <span> element containing the text.
   * @memberof HighlightTextComponent
   */
  private createNormalTextElement(text: string) {
    const element: HTMLSpanElement = this.renderer.createElement('span');
    element.textContent = text;
    return element;
  }

  /**
   * Makes a marked text element in a <span> tag with the highlight class.
   *
   * @private
   * @param {string} text The text to wrap in the tag to highlight.
   * @returns A <span> element containing the text, marked.
   * @memberof HighlightTextComponent
   */
  private createMarkedTextElement(text: string) {
    const element: HTMLSpanElement = this.renderer.createElement('span');
    element.textContent = text;
    element.classList.add('MarkedText');
    return element;
  }

  /**
   * Render the element passed in to the container element.
   *
   * @private
   * @param {HTMLSpanElement} element The element to render.
   * @memberof HighlightTextComponent
   */
  private renderElementToContainer(element: HTMLSpanElement) {
    this.renderer.appendChild(this.container.nativeElement, element);
  }

  /**
   * Clears all elements from the container.
   *
   * @private
   * @memberof HighlightTextComponent
   */
  private clearElements() {
    const container = this.container.nativeElement;
    Array.from(container.childNodes).forEach((node: Node) => {
      this.renderer.removeChild(container, node);
    });
  }

  /**
   * Generate the text with the highlight on the text to be marked.
   *
   * @private
   * @param {string} text The entire text.
   * @param {string} textToMark The particular text to highlight.
   * @memberof HighlightTextComponent
   */
  private generateTextWithHighlight(
    text: string = '',
    textToMark: string = '',
  ) {
    this.clearElements();

    if (!textToMark || textToMark.trim() === '') {
      const element = this.createNormalTextElement(text);
      return this.renderElementToContainer(element);
    }

    text
      .split(new RegExp(`(${escapeRegExp(textToMark)})`, 'i'))
      .map((extract: string) => {
        if (extract.toLowerCase() === textToMark.toLowerCase()) {
          return this.createMarkedTextElement(extract);
        }

        return this.createNormalTextElement(extract);
      })
      .forEach(element => this.renderElementToContainer(element));
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-i18n-webapp-compat/LANG/it/webapp/src/app/@shared/ui/highlight-text/highlight-text.component.ts